import type { NextPage, NextPageContext } from 'next';
import { AccountLayout } from '@kiwicom/account-components';
import * as serverCookies from '@kiwicom/cookies/server';
import { events, logServer } from '@kiwicom/account-tracking';
import { VerifyEmail } from '@kiwicom/account-auth';
import { Cookie } from '@kiwicom/cookies';

import verifyEmail from '../../../../../dataFetching/verifyEmail';
import fetchUserInfo from '../../../../../dataFetching/fetchUserInfo';
import { normalizeQueryParam } from '../../../../../src/util';

type Props = {
  readonly error?: string;
};

const VerifyEmailPage: NextPage<Props> = ({ error }) => {
  return (
    <AccountLayout>
      <VerifyEmail error={error} />
    </AccountLayout>
  );
};

VerifyEmailPage.getInitialProps = async (ctx: NextPageContext & { clientIp: string }) => {
  const token = normalizeQueryParam(ctx?.query?.token);
  const language = ctx?.query?.language ?? 'en';
  const res = ctx?.res;
  const req = ctx?.req;
  const {
    utm_campaign: utmCampaign,
    utm_medium: utmMedium,
    utm_content: utmContent,
    utm_source: utmSource,
  } = ctx?.query;

  const clientIp = ctx?.clientIp;
  const globals = {
    ip: clientIp ?? '',
  };
  const eventProps = {
    userAgent: req?.headers?.['user-agent'],
    referer: req?.headers?.referer,
    utmCampaign: normalizeQueryParam(utmCampaign),
    utmMedium: normalizeQueryParam(utmMedium),
    utmContent: normalizeQueryParam(utmContent),
    utmSource: normalizeQueryParam(utmSource),
  };
  let authToken = null;

  try {
    const response = await verifyEmail(token, clientIp);
    ({ authToken } = response);
  } catch (error) {
    if (error === 'INVALID_ARGUMENT_EMAIL_TOKEN') {
      void logServer(events.verifyEmail.INVALID_TOKEN, { ...eventProps }, globals);
    } else {
      void logServer(
        events.verifyEmail.GENERAL_ERROR,
        { ...eventProps, error: (error as Error).message },
        globals,
      );
    }

    return {
      isSecuredPage: false,
      error: (error as Error).message,
    };
  }

  void logServer(events.verifyEmail.SUCCESSFULL_VERIFICATION, { ...eventProps }, globals);
  let viewer = null;

  if (authToken != null) {
    try {
      const userInfo = await fetchUserInfo(authToken);

      if (userInfo?.viewer?.__typename === 'User') {
        ({ viewer } = userInfo);
      }
    } catch (error) {
      void logServer(
        events.verifyEmail.ACCOUNT_GRAPHQl_ERROR,
        { ...eventProps, error: (error as Error).message },
        globals,
      );
      return {
        isSecuredPage: false,
        error: (error as Error).message,
      };
    }
  }

  if (authToken != null && res != null && req != null) {
    const languageId: string = viewer?.locale?.language?.languageId ?? language;
    const currencyId: string = viewer?.locale?.currency?.code;
    serverCookies.save({
      key: Cookie.UA_SESSION_TOKEN,
      value: authToken,
      req,
      res,
    });
    serverCookies.save({
      key: Cookie.LANGUAGE,
      value: languageId,
      req,
      res,
    });

    if (currencyId != null) {
      serverCookies.save({
        key: Cookie.CURRENCY,
        value: currencyId,
        req,
        res,
      });
    }

    void logServer(events.verifyEmail.REDIRECT_TO_HOMEPAGE, { ...eventProps }, globals);
    res.writeHead(302, {
      Location: `/${languageId}/user`,
    });
    res.end();
  }

  return {
    isSecuredPage: false,
  };
};

export default VerifyEmailPage;
